/**
 * CSV Export Utility
 * 
 * Extracts table data from HTML and exports to CSV file
 */

/**
 * Extract table data from HTML table element
 */
export function extractTableData(tableElement: HTMLTableElement): string[][] {
  const rows: string[][] = [];
  
  // Extract headers from thead
  const thead = tableElement.querySelector('thead');
  if (thead) {
    const headerRow: string[] = [];
    const headerCells = thead.querySelectorAll('th');
    headerCells.forEach(cell => {
      headerRow.push(cell.textContent?.trim() || '');
    });
    if (headerRow.length > 0) {
      rows.push(headerRow);
    }
  }
  
  // Extract data from tbody
  const tbody = tableElement.querySelector('tbody');
  if (tbody) {
    const dataRows = tbody.querySelectorAll('tr');
    dataRows.forEach(row => {
      const rowData: string[] = [];
      const cells = row.querySelectorAll('td, th');
      cells.forEach(cell => {
        rowData.push(cell.textContent?.trim() || '');
      });
      if (rowData.length > 0) {
        rows.push(rowData);
      }
    });
  } else {
    // If no tbody, extract from table directly
    const dataRows = tableElement.querySelectorAll('tr');
    dataRows.forEach((row, index) => {
      // Skip first row if we already got headers from thead
      if (thead && index === 0) return;
      
      const rowData: string[] = [];
      const cells = row.querySelectorAll('td, th');
      cells.forEach(cell => {
        rowData.push(cell.textContent?.trim() || '');
      });
      if (rowData.length > 0) {
        rows.push(rowData);
      }
    });
  }
  
  return rows;
}

/**
 * Convert table data to CSV string
 */
export function tableDataToCSV(data: string[][]): string {
  return data.map(row => {
    return row.map(cell => {
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      const escaped = cell.replace(/"/g, '""');
      if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
        return `"${escaped}"`;
      }
      return escaped;
    }).join(',');
  }).join('\n');
}

/**
 * Download CSV file
 */
export function downloadCSV(csvContent: string, filename: string = 'export.csv'): void {
  // Add BOM for Excel UTF-8 support
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    // Create download link
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Export table element to CSV
 */
export function exportTableToCSV(tableElement: HTMLTableElement, filename: string = 'table-export.csv'): void {
  const data = extractTableData(tableElement);
  if (data.length === 0) {
    throw new Error('No data found in table');
  }
  const csv = tableDataToCSV(data);
  downloadCSV(csv, filename);
}

/**
 * Find all tables in a container and add export buttons
 */
export function addExportButtonsToTables(container: HTMLElement, filenamePrefix: string = 'data'): void {
  const tables = container.querySelectorAll('table');
  
  tables.forEach((table, index) => {
    // Check if button already exists
    const wrapper = table.parentElement;
    if (!wrapper) return;
    
    const existingButton = wrapper.querySelector('.csv-export-btn');
    if (existingButton) return;
    
    // Create a wrapper div for table and button if not already wrapped
    let tableWrapper = wrapper;
    if (!wrapper.classList.contains('table-with-export')) {
      tableWrapper = document.createElement('div');
      tableWrapper.className = 'table-with-export';
      tableWrapper.style.marginBottom = '1rem';
      
      // Wrap the table
      wrapper.insertBefore(tableWrapper, table);
      tableWrapper.appendChild(table);
    }
    
    // Create export button
    const button = document.createElement('button');
    button.className = 'btn btn-sm btn-outline-primary csv-export-btn';
    button.style.marginTop = '0.5rem';
    button.style.marginBottom = '0.5rem';
    button.style.float = 'right';
    button.innerHTML = '<i class="fas fa-download mr-1"></i> Export to CSV';
    button.title = 'Export this table to CSV file';
    
    button.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        const filename = `${filenamePrefix}-${index + 1}-${new Date().toISOString().split('T')[0]}.csv`;
        exportTableToCSV(table as HTMLTableElement, filename);
      } catch (error) {
        console.error('CSV export failed:', error);
        alert('Failed to export table to CSV');
      }
    };
    
    // Append button after table
    tableWrapper.appendChild(button);
    
    // Add clearfix to ensure proper layout
    const clearfix = document.createElement('div');
    clearfix.style.clear = 'both';
    tableWrapper.appendChild(clearfix);
  });
}